import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import { fetchAuditFeed, groupAuditRows, type AuditRow } from "../data/audit";
import { fetchAuditTaxonomy, type AuditTaxonomyEntry } from "../data/audit-taxonomy";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";

const COLLAPSED_COUNT = 15;

export function AuditFeed({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
}) {
  const { status, data, error } = useReadModel<AuditRow[]>(
    () => fetchAuditFeed(client, accountId),
    [client, accountId],
    pollMs,
  );
  const taxonomy = useReadModel<Map<string, AuditTaxonomyEntry>>(
    () => fetchAuditTaxonomy(client),
    [client],
    0,
  );
  const [expanded, setExpanded] = useState(false);

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading audit feed…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load the audit feed."}
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return <p className="surface__state" role="status">No recent activity.</p>;
  }

  const grouped = groupAuditRows(rows);
  const visible = expanded ? grouped : grouped.slice(0, COLLAPSED_COUNT);
  const hiddenCount = grouped.length - visible.length;

  return (
    <>
      <ul className="audit-grid">
        {visible.map(({ row, count }, index) => {
          const entry = taxonomy.data?.get(row.eventType);
          return (
            <li
              key={row.auditEventId || `${row.createdAt ?? "event"}-${index}`}
              className="audit-card"
            >
              <div className="audit-card__head">
                <span className="audit-card__event">{humanizeLabel(row.eventType)}</span>
                {count > 1 ? <span className="audit-card__count">×{count}</span> : null}
              </div>
              <p className="audit-card__meta">
                <span className="audit-card__actor">{row.actorLabel ?? "System"}</span>
                {row.resourceType ? (
                  <span className="audit-card__resource">{humanizeLabel(row.resourceType)}</span>
                ) : null}
              </p>
              <div className="audit-card__foot">
                <div className="audit-card__chips">
                  {entry ? (
                    <span className="audit__tax audit__tax--category">{humanizeLabel(entry.category)}</span>
                  ) : null}
                  {entry && entry.sensitivity !== "standard" ? (
                    <span className={`audit__tax audit__tax--${entry.sensitivity}`}>
                      {humanizeLabel(entry.sensitivity)}
                    </span>
                  ) : null}
                </div>
                <time className="audit-card__when" dateTime={row.createdAt ?? undefined}>
                  {formatDisplayTime(row.createdAt, timeZone)}
                </time>
              </div>
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 ? (
        <button type="button" className="builder__navbtn audit__more" onClick={() => setExpanded(true)}>
          Show {hiddenCount} more
        </button>
      ) : null}
    </>
  );
}
